import { cn } from '@/lib/utils'
import type { LabelPosition, LabelWidth } from '@/types/cgt'
import {
  LABEL_POSITIONS,
  LABEL_POSITION_LABELS,
  LABEL_WIDTHS,
  LABEL_WIDTH_LABELS,
} from '@/lib/fieldLayout'
import { LabelWidthSkeleton } from './WidthSkeleton'

/**
 * Label position and label width pickers (z8nrz7djyx), used for one field in
 * the Properties panel and for the form default in Form Settings. The width
 * picker hides while labels sit above, since width means nothing there; the
 * stored width is kept, so switching back restores it.
 */
export function LabelLayoutPicker({
  position,
  width,
  onPositionChange,
  onWidthChange,
}: {
  position: LabelPosition
  width: LabelWidth
  onPositionChange: (position: LabelPosition) => void
  onWidthChange: (width: LabelWidth) => void
}) {
  return (
    <div className='space-y-3'>
      <div className='space-y-1.5'>
        <p className='text-sm font-medium leading-none'>Label position</p>
        <div role='group' aria-label='Label position' className='grid grid-cols-3 gap-0.5 rounded-md border p-0.5'>
          {LABEL_POSITIONS.map(p => (
            <button
              key={p}
              type='button'
              aria-pressed={position === p}
              className={cn(
                'rounded px-2 py-1 text-xs hover:bg-accent',
                position === p ? 'bg-accent font-medium' : 'text-muted-foreground',
              )}
              onClick={() => onPositionChange(p)}
            >
              {LABEL_POSITION_LABELS[p]}
            </button>
          ))}
        </div>
      </div>
      {position !== 'above' && (
        <div className='space-y-1.5'>
          <p className='text-sm font-medium leading-none'>Label width</p>
          <div role='group' aria-label='Label width' className='grid grid-cols-3 gap-0.5'>
            {LABEL_WIDTHS.map(w => (
              <button
                key={w}
                type='button'
                aria-pressed={width === w}
                className={cn(
                  'flex flex-col items-center gap-1 rounded border px-1.5 py-1.5 text-[11px] hover:bg-accent',
                  width === w ? 'border-primary bg-accent font-medium' : 'border-transparent',
                )}
                onClick={() => onWidthChange(w)}
              >
                <LabelWidthSkeleton width={w} />
                {LABEL_WIDTH_LABELS[w]}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
